// DO NOT RENAME THESE
// These are used in telemetry.utils.ts as context's when logging to TAKT. The log function will automatically append `ihub-`
// onto the front of the value if it doesn't exist, this allows our Cradle Job to find and export these events properly to QuickSight

import type { AllUnifiedFilterKeys } from "../types/filters/unified";

// Cradle Job: https://datacentral.a2z.com/cradle#/client-deck-builder-prod/profiles/e31047f1-e7db-4982-a803-ff1931eb86c9
export const TELEMETRY_ID = {
    PREVIEW_SLIDE_COUNT: "insight-preview-slide-count",
    PREVIEW_ERROR: "insight-preview-error",
    INSIGHT_DOWNLOAD: "insight-download",
    CURATE_INSIGHT_REQUEST: "curate-insight-request",
    CUSTOM_INSIGHT_REQUEST: "custom-insight-request",
    INSIGHT_LINKAGE_REQUEST: "insight-linkage-request",
    ADDED_TO_WB: "added-to-workbench",
    CAIRE_LINK_TO_APB: "packages-link-to-apb",
    INSIGHT_CARD: "insight-card",
    INSIGHT_DETAILS: "insight-details",
    INSIGHT_LINKAGE_FEEDBACK_CLICKED: "insight-linkage-feedback-clicked",
    TYPE_OPTION_SELECTION: "type-option-selected",
    FILTER_OPTION_SELECTION: "filter-option-selected",
    NUMBER_OF_RESULTS: "number-of-results",
    VIEW_INSIGHT_DETAILS_CLICK: "view-insight-details-click",
    CLEAR_ADDED_FILTERS_CLICK: "clear-added-filters-click",
    ALL_INSIGHTS_CONTENT_TYPE_CHANGE: "content-type-change",
    YOUR_INSIGHTS_CONTENT_TYPE_CHANGE: "your-insights-content-type-change",
    TOP_NAV_BAR_SELECTION: "top-nav-bar-selection",
    DISCOVERY_ERROR_PAGE: "discovery-error-page",
    SHARE_FILTER_CLICK: "share-filter-clicks",
    PITCHED_CONTENT_UPLOAD_CLICK: "pitched-content-upload",
    PITCHED_CONTENT_EDIT_CLICK: "pitched-content-edit",
    PITCHED_CONTENT_GENERATE_SHARE_LINK: "pitched-content-generate-share-link",
    PITCHED_CONTENT_SHARE_URL: "pitched-content-share-url",
    PITCHED_CONTENT_SHARE_PASSCODE: "pitched-content-share-passcode",
    INSIGHT_REFRESH_CLICK: "insight-refresh-status-click",
    INSIGHT_REFRESH_WARNING_OPEN: "insight-refresh-warning-open",
    TIMERS: {
        // logWithTakt will auto pre-pends `ihub-` to our telemetry, but this is called in a separate function
        // So to maintain backwards compatibility we left the ihub
        APP_LOAD_TO_PAGE_CLOSE: "ihub-app-load-to-page-close",
    },
    FEEDBACK_SUBMITTED: "feedback-submitted",
    ACCOUNT_AGNOSTIC_XWAI_VISITED: "account-agnostic-xwai-visited",
    G4S_CONTENT_TYPES_FILTER_APPLIED: "g4s-content-types-filter-applied",
    G4S_MARKETING_CONTENT_FEEDBACK_SUBMITTED:
        "g4s-marketing-content-feedback-submitted",
    G4S_INSIGHT_PROMPT_SUBMITTED: "g4s-insight-prompt-submitted",
    G4S_EMAIL_PROMPT_SUBMITTED: "g4s-email-prompt-submitted",
    G4S_COPY_EMAIL_TO_CLIPBOARD: "g4s-email-clipboard",
    ACCOUNT_PICKER_OPEN: "account-picker-open",
    ACCOUNT_PICKER_SELECTION: "account-picker-selection",
    ACCOUNT_PICKER_CANCEL: "account-picker-cancel",
    ACCOUNT_SWITCHER_SELECTION: "account-switcher-selection",
    ACCOUNT_SWITCH_SUCCESS: "account-switch-success",
    ACCOUNT_DROPDOWN_SEARCH: "account-dropdown-search",
    ACCOUNT_DROPDOWN_SELECTION: "account-dropdown-selection",
    ACCOUNT_DROPDOWN_MANAGE_LINK_CLICK: "account-dropdown-manage-link-click",
    ACCOUNT_DROPDOWN_IHUB_LINK_CLICK: "account-dropdown-ihub-link-click",

    // Template-specific account picker telemetry
    TEMPLATE_ACCOUNT_PICKER_OPEN: "template-account-picker-open",
    TEMPLATE_ACCOUNT_PICKER_SELECTION: "template-account-picker-selection",
    TEMPLATE_ACCOUNT_PICKER_CANCEL: "template-account-picker-cancel",
    TEMPLATE_ACCOUNT_SWITCH_SUCCESS: "template-account-switch-success",
    TEMPLATE_ACCOUNT_ACCESS_DENIED: "template-account-access-denied",

    INSIGHT_SHARE_SUCCESS: "insight-share-success",
    CSAT_APP_FEEDBACK_CANCELLED: "csat-app-feedback-cancelled",
    CSAT_APP_FEEDBACK_SUBMITTED: "csat-app-feedback-submitted",
    IHUB_CUSTOM_INSIGHTS_RAPID_REPEAT: "custom-insight-rapid-repeat",
    IHUB_CUSTOM_INSIGHTS_CLONE: "custom-insight-clone",
    IHUB_CUSTOM_INSIGHTS_HYDRATION_WARNING: "custom-insight-hydration-warning",
    IHUB_CUSTOM_INSIGHTS_HYDRATION_DIMENSION_WARNING:
        "custom-insight-hydration-dimension-warning",
    IHUB_CUSTOM_INSIGHTS_HYDRATION_SUCCESS: "custom-insight-hydration-success",
    G4S_DECK_DECORATE_PROMPT_SUBMITTED: "g4s-deck-decorate-prompt-submitted",
    VIEW_G4S_RELEVANT_INSIGHTS_CLICKED: "g4s-view-relevant-insights-click",
    G4S_COPY_SUMMARY_TO_CLIPBOARD: "g4s-summary-clipboard",
    ASYNC_OFFICE_CONVERT_CACHE_HIT: "async-office-convert-cache-hit",
    ASYNC_OFFICE_CONVERT_CACHE_MISS: "async-office-convert-cache-miss",
    ASYNC_OFFICE_CONVERT_ERROR: "async-office-convert-error",
    ASYNC_OFFICE_CONVERT_DURATION_OVER_1_MIN:
        "async-office-convert-duration-over-1-min",
    ASYNC_OFFICE_CONVERT_DURATION_OVER_2_MIN:
        "async-office-convert-duration-over-2-min",
    PAGE_LOAD: "page-load",
    FILTER_PILL_ADDED: "search-filter-pill-added",
    FILTER_PILL_REMOVED: "search-filter-pill-removed",
    THUMBS_FEEDBACK_CLICKED: "thumbs-feedback-clicked",
    THUMBS_FEEDBACK_SUBMITTED: "thumbs-feedback-submitted",
    THUMBS_FEEDBACK_CANCELLED: "thumbs-feedback-cancelled",
    REVERT_TO_LEGACY_EXPERIENCE: "revert-to-legacy-search-experience",
    CONTACT_US_SHARE_FEEDBACK_FORM_CLOSED:
        "contact-us-share-feedback-form-closed",
    CONTACT_US_SHARE_FEEDBACK_FORM_SUBMITTED:
        "contact-us-share-feedback-form-submitted",
    CONTACT_US_SHARE_FEEDBACK_FORM_CLICKED:
        "contact-us-share-feedback-form-clicked",
    CONTACT_US_REPORT_ISSUE_CLICKED: "contact-us-report-issue-clicked",
    CONTACT_US_FEATURE_REQUEST_CLICKED: "contact-us-feature-request-clicked",
    CONTACT_US_FEATURE_REQUEST_SUBMITTED:
        "contact-us-feature-request-submitted",
    CONTACT_US_FEATURE_REQUEST_CANCELLED:
        "contact-us-feature-request-cancelled",
    CONTACT_US_CONTENT_REQUEST_CLICKED: "contact-us-content-request-clicked",
    CONTACT_US_USER_GUIDE_CLICKED: "contact-us-user-guide-clicked",
    AI_SUMMARY_DROPDOWN_CLICK: "ai-summary-dropdown-click",
    AI_SUMMARY_SHOW_MORE_CLICK: "ai-summary-show-more-click",
    SEARCHED_FOR_CONTENTS: "searched-for-contents",
    UNIFIED_SEARCH_SORT_SELECTION: "unified-search-sort-selection",
    QUICK_FILTER: {
        CREATE: "quick-filter-create",
        DELETE: "quick-filter-delete",
        APPLY: "quick-filter-apply",
        UPDATE: "quick-filter-update",
    },
    FORGE: {
        VIZ_DOWNLOAD: "forge-viz-download",
        VIZ_DOWNLOAD_SUCCESS: "forge-viz-download-success",
        VIZ_DOWNLOAD_ERROR: "forge-viz-download-error",
        BETA_OPTIN: "forge-beta-optin",
        BETA_OPTOUT: "forge-beta-optout",
        ENABLED_OPTED_IN: "forge-enabled-opted-in",
        ENABLED_OPTED_OUT: "forge-enabled-opted-out",
        DISABLED_OPTED_IN: "forge-disabled-opted-in",
        DISABLED_OPTED_OUT: "forge-disabled-opted-out",
    },
};

/* 
The following is used to emit Cloudwatch RUM metrics via
https://w.amazon.com/bin/view/SSPA/AX/ACPT/Telemetry/Documentation/CloudwatchRUMAccess
*/
export const CLOUDWATCH_RUM_COUNTERS = {
    UNHANDLED_CLIENT_ERROR_BOUNDARY: "Error.UnhandledClientErrorBoundary",
    PDF_PREVIEW_ERROR: "Error.PdfPreviewError",
};

export const TELEMETRY_OPERATIONS = {
    GET_DROPDOWN_VALUES: "API.getDropdownValues",
    GET_SF_OPPORTUNITIES: "API.getSfOpportunities",
    GET_SF_ACTIVITIES: "API.getSfActivities",
    GET_SF_ACCOUNTS: "API.getSfAccounts",
    GET_INPUT_SOURCES_BY_ADVERTISER_ID: "API.getInputSourcesByAdvertiserId",
    GET_STANDALONE_PACKAGES: "API.getStandalonePackages",
    GET_GENERATED_INSIGHTS: "API.getGeneratedInsights",
    GET_GENERATED_INSIGHT: "API.getGeneratedInsight",
    CREATE_PACKAGED_INSIGHTS: "API.createPackagedInsight",
    CREATE_CURATED_INSIGHT: "API.createCuratedInsight",
    GET_GENERATED_INSIGHT_DOWNLOAD: "API.getGeneratedInsightDownload",
    GET_STANDALONE_PACKAGE_DOWNLOAD: "API.getStandalonePackageDownload",
    GET_INSIGHT_FAMILY_DOWNLOAD: "API.getInsightFamilyDownload",
    GET_INSIGHT_FAMILY: "API.getInsightFamily",
    GET_INSIGHT_FAMILIES: "API.getInsightFamilies",
    GET_SF_CONTEXT: "API.getSfContext",
    CREATE_INSIGHT_LINKAGE: "API.createInsightLinkage",
    GET_INSIGHT_FAMILIES_V2: "API.getInsightFamiliesV2",
    GET_INSIGHT_FAMILIES_FILTER_METADATA:
        "API.getInsightFamiliesFilterMetadata",
    GET_NON_ENDEMIC_TARGET_SEGMENTS: "API.getNonEndemicTargetSegments",
    GET_NON_ENDEMIC_TARGET_SEGEMENT_TYPES:
        "API.getNonEndemicTargetSegmentTypes",
};

// Phoenix-specific telemetry event IDs (used only for Phoenix metrics, not Takt)
export const PHOENIX_TELEMETRY_ID = {
    SEARCHED_FOR_CONTENTS_ENTER: "searched-for-contents-enter",
    SEARCHED_FOR_CONTENTS_BUTTON: "searched-for-contents-button",
    ACCOUNT_DROPDOWN_SEARCH_COUNT: "account-dropdown-search-count",
    AI_SUMMARY_SHOW_MORE_CLICK: "ai-summary-show-more-click",
    AI_SUMMARY_SHOW_MORE_CLICK_HIDE: "ai-summary-show-more-click-hide",
};

export const PHOENIX_FEATURE = {
    NAVIGATION: "Navigation",
    DISCOVERY: "Discovery",
    WORKBENCH: "Workbench",
    FEEDBACK: "Feedback",
    AI: "AI",
    FORGE: "Forge",
    FILTERS: "Filters",
    QUICK_FILTERS: "QuickFilters",
    CONTENT: "Content",
    ACCOUNT_PICKER: "AccountPicker",
    MISCELLANEOUS: "Miscellaneous",
};

export type UnifiedFilterTelemetry = {
    [k in AllUnifiedFilterKeys]: TelemetryFilterModel[];
};
export type TelemetryFilterModel = {
    id: string;
    label: string;
};
